import { ElementRef } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { TableColumnAnomalyComponent } from '@dice/app/components/atoms/table-columns/table-column-anomaly/table-column-anomaly.component';
import { TableColumnAnomalyDetailComponent } from '@dice/app/components/atoms/table-columns/table-column-anomaly-detail/table-column-anomaly-detail.component';
import { TableColumnBooleanComponent } from '@dice/app/components/atoms/table-columns/table-column-boolean/table-column-boolean.component';
import { TableColumnButtonComponent } from '@dice/app/components/atoms/table-columns/table-column-button/table-column-button.component';
import { TableColumnCheckboxComponent } from '@dice/app/components/atoms/table-columns/table-column-checkbox/table-column-checkbox.component';
import { TableColumnCheckbox2Component } from '@dice/app/components/atoms/table-columns/table-column-checkbox2/table-column-checkbox2.component';
import { TableColumnDropDownComponent } from '@dice/app/components/atoms/table-columns/table-column-drop-down/table-column-drop-down.component';
import { TableColumnFlyoutMenuComponent } from '@dice/app/components/atoms/table-columns/table-column-flyout-menu/table-column-flyout-menu.component';
import { TableColumnIconComponent } from '@dice/app/components/atoms/table-columns/table-column-icon/table-column-icon.component';
import { TableColumnImgComponent } from '@dice/app/components/atoms/table-columns/table-column-img/table-column-img.component';
import { TableColumnLinkComponent } from '@dice/app/components/atoms/table-columns/table-column-link/table-column-link.component';
import { TableColumnNgTemplateComponent } from '@dice/app/components/atoms/table-columns/table-column-ng-template/table-column-ng-template.component';
import { TableColumnOpenhourDetailComponent } from '@dice/app/components/atoms/table-columns/table-column-openhour-detail/table-column-openhour-detail.component';
import { TableColumnPrerenderedTextComponent } from '@dice/app/components/atoms/table-columns/table-column-prerendered-text/table-column-prerendered-text.component';
import { TableColumnRadioButtonComponent } from '@dice/app/components/atoms/table-columns/table-column-radio-button/table-column-radio-button.component';
import { TableColumnRolesComponent } from '@dice/app/components/atoms/table-columns/table-column-roles/table-column-roles.component';
import { TableColumnSwitchComponent } from '@dice/app/components/atoms/table-columns/table-column-switch/table-column-switch.component';
import { TableColumnTextComponent } from '@dice/app/components/atoms/table-columns/table-column-text/table-column-text.component';
import { TableColumnTextHtmlComponent } from '@dice/app/components/atoms/table-columns/table-column-text-html/table-column-text-html.component';
import { TableButtonsGroupComponent } from '@dice/app/components/molecules/table-buttons-group/table-buttons-group.component';
import { MatMultiSortTableDataSource } from '@dice/app/components/organisms/mat-multi-sort/mat-multi-sort-data-source';
import { TableDataSource } from '@dice/app/directives/table-data-source';
import CoreTableDataSource from '@dice/app/libraries/core-table-virtual-scrolling/data-source';
import { BinaryFunction, UnaryFunction } from '@dice/app/types/dice.types';
import { FlyOutMenuButton } from '@dice/app/types/search-bar.types';
import { Role } from '@home/app/components/types/user.types';
import { Observable } from 'rxjs';

export type RowData = any;

export interface TableConfiguration {
  headerRow?: RowConfiguration;
  tableRow?: RowConfiguration;
  detailsRow?: DetailsContent;
}

interface RowConfiguration {
  cssClass?: UnaryFunction<RowData, string> | string[] | string;
  click?: UnaryFunction<RowData>;
  dblClick?: UnaryFunction<RowData>;
}

export type DetailsContent = TableColumnAnomalyDetailContent | TableColumnOpenhourDetailContent;

export type HeaderContent = TableColumnTextTranslationContent | TableButtonsGroupContent | TableColumnIconContent;

export type RowContent =
  | TableButtonsGroupContent
  | TableColumnSwitchComponentContent
  | TableColumnDropDownComponentContent
  | TableColumnIconContent
  | TableColumnImgContent
  | TableColumnRolesContent
  | TableColumnLinkContent
  | TableColumnAnomalyContent
  | TableColumnTextDisplayValueContent
  | TableColumnPrerenderedTextContent
  | TableColumnNgTemplateContent
  | TableColumnBooleanContent;

export interface Column {
  name?: string;
  header?: HeaderContent;
  table?: RowContent;
  disableSorting?: boolean;
  dataSelector?: string;
}

/** *                                            * */
/** *          Component Definitions             * */

export interface ColumnComponentType<T> {
  /**
   * The implementing Component has to define this as __static__.
   */
  sortEnabled: boolean;
  /**
   * The current row element.
   */
  element?: T;
}

interface BaseContent<T = RowData> {
  type: ColumnComponentType<T>;
  columnCssClass?: string[] | string;
  cssClass?: UnaryFunction<T, string> | string[] | string;
}

interface TableColumnAnomalyDetailContent {
  type: typeof TableColumnAnomalyDetailComponent;
}

interface TableColumnOpenhourDetailContent {
  type: typeof TableColumnOpenhourDetailComponent;
}

interface TableColumnTextContent<T = RowData> extends BaseContent {
  type: typeof TableColumnTextComponent | typeof TableColumnTextHtmlComponent;
  tooltip?: UnaryFunction<T, string> | string;
}

interface TableColumnPrerenderedTextContent extends BaseContent {
  type: typeof TableColumnPrerenderedTextComponent;
}

interface TableColumnTextTranslationContent extends TableColumnTextContent {
  translation?: string;
  translationTooltip?: string;
}

interface TableColumnTextDisplayValueContent extends TableColumnTextContent {
  displayValue: string | UnaryFunction<RowData, string>;
  tooltip?: string | UnaryFunction<RowData, string>;
}

interface TableColumnSwitchComponentContent extends BaseContent {
  type: typeof TableColumnSwitchComponent;
  id?: number | string;
  checked?: UnaryFunction<RowData, boolean> | boolean;
  onClick?: UnaryFunction<RowData>;
  disabled?: UnaryFunction<RowData, boolean> | boolean;
}

interface TableColumnDropDownComponentContent extends BaseContent {
  type: typeof TableColumnDropDownComponent;
  selectedValue: UnaryFunction<RowData, string>;
  availableValues: UnaryFunction<RowData, Observable<string[]>>;
  selectedValueChanged: BinaryFunction<RowData, string>;
  isDisabled: UnaryFunction<RowData, boolean>;
}

interface TableColumnAnomalyContent extends BaseContent {
  type: typeof TableColumnAnomalyComponent;
  displayValue: string | UnaryFunction<RowData, string>;
  isSeverity?: boolean;
}

interface TableColumnImgContent extends BaseContent {
  type: typeof TableColumnImgComponent;
  displayValue: string;
}

interface TableColumnRolesContent extends BaseContent {
  type: typeof TableColumnRolesComponent;
  roles?: Role[];
}

interface TableColumnIconContent<T = RowData> extends BaseContent {
  type: typeof TableColumnIconComponent;
  iconName: string | UnaryFunction<RowData, string>;
  tooltip?: UnaryFunction<T, string> | string;
  tooltipClass?: UnaryFunction<T, string> | string;
  noSdxIcon?: UnaryFunction<T, boolean> | boolean;
  disabled?: UnaryFunction<T, boolean> | boolean;
  hidden?: UnaryFunction<T, boolean> | boolean;
}

interface TableColumnLinkContent extends BaseContent {
  type: typeof TableColumnLinkComponent;
  displayValue?: string | UnaryFunction<RowData, string>;
}

export type ButtonContent =
  | TableColumnCheckboxContent
  | TableColumnCheckbox2Content
  | TableColumnButtonContent
  | TableColumnRadioButtonContent
  | TableColumnFlyoutMenuButtonContent;

interface TableButtonsGroupContent extends BaseContent {
  type: typeof TableButtonsGroupComponent;
  buttons: ButtonContent[];
}

interface TableColumnCheckboxContent extends BaseContent {
  type: typeof TableColumnCheckboxComponent;
  checked?: UnaryFunction<RowData, boolean> | boolean;
  onChange?: UnaryFunction<RowData>;
  changed?: BinaryFunction<RowData, boolean>;
  permission?: string;
}

interface TableColumnCheckbox2Content extends BaseContent {
  type: typeof TableColumnCheckbox2Component;
  selectionAttribute?: string;
  onChange?: BinaryFunction<RowData, boolean>;
  permission?: string;
}

interface TableColumnRadioButtonContent extends BaseContent {
  type: typeof TableColumnRadioButtonComponent;
  checked: UnaryFunction<RowData, boolean> | boolean;
  onChange: UnaryFunction<RowData>;
  permission?: string;
  model?: boolean;
  sortEnabled?: boolean;
}

interface TableColumnButtonContent extends BaseContent {
  type: typeof TableColumnButtonComponent;
  onClick?: UnaryFunction<RowData>;
  iconName?: string | UnaryFunction<RowData, string>;
  permission?: string;
  show?: UnaryFunction<RowData, boolean>;
  isSdxIcon?: boolean;
  disabled?: boolean | UnaryFunction<RowData, boolean>;
  hidden?: boolean | UnaryFunction<RowData, boolean>;
  tooltip?: string | UnaryFunction<RowData, string>;
}

interface TableColumnFlyoutMenuButtonContent extends BaseContent {
  type: typeof TableColumnFlyoutMenuComponent;
  iconName?: string | UnaryFunction<RowData, string>;
  buttons: FlyOutMenuButton[];
  disabled?: boolean;
}

interface TableColumnNgTemplateContent extends BaseContent {
  type: typeof TableColumnNgTemplateComponent;
  template: ElementRef;
}

interface TableColumnBooleanContent extends BaseContent {
  type: typeof TableColumnBooleanComponent;
}

/** *                                            * */
/** *                                            * */

export interface PaginatorOptions {
  backendPagination?: boolean;
  pageSize?: number;
  pageChangedCallback?: UnaryFunction<PageEvent>;
  pageIndex?: number;
  showFirstLastButtons?: boolean;
  hidePageSize?: boolean;
  pageLength?: number;
  pageSizeOptions?: number[];
}

export interface TableSortDefinition {
  direction: 'asc' | 'desc';
  columnName: string;
}

export interface TableSortInterface<T> {
  columns?: Column[];
  dataSource: MatTableDataSource<T> | CoreTableDataSource<T> | TableDataSource<T>;
  defaultSorting?: TableSortDefinition;
  disableSort?: boolean;
}

export abstract class TableSortComponent<T> implements TableSortInterface<T> {
  columns?: Column[];

  dataSource: MatTableDataSource<T> | CoreTableDataSource<T> | TableDataSource<T>;

  defaultSorting?: TableSortDefinition;

  disableSort?: boolean = false;
}

export interface TableFilterInterface<T> {
  columns?: Column[];
  dataSource: MatTableDataSource<T> | MatMultiSortTableDataSource<T> | CoreTableDataSource<T> | TableDataSource<T>;
  filterPredicate?: (data: T, filter: string) => boolean;
  searchTerm: string;
}

export abstract class TableFilterComponent<T> implements TableFilterInterface<T> {
  columns?: Column[];

  dataSource: MatTableDataSource<T> | CoreTableDataSource<T> | TableDataSource<T>;

  filterPredicate?: FilterPredicateFn<T>;

  searchTerm: string;
}

export type FilterPredicateFn<T> = (data: T, filter: string, filters?: TableColumnFilter[]) => boolean;

export type DisplayValueFn<T> = (value: T) => string | number;

export interface TableFilter {
  search: string;
  filters: TableColumnFilter[];
}

export class TableColumnFilter {
  keyPath: string[];

  filter: string;

  constructor(...keyPath: string[]) {
    this.keyPath = keyPath;
  }
}
